import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import Badge, { KINDS, SIZES } from '@medflex/components/stories/Badge';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import { Link } from 'react-router-dom';
import UserInfo from '@medflex/components/stories/UserInfo';
import getInitials from '@medflex/utils/getInitials';
import formatDateOfBirth from '@medflex/utils/getDateOfBirthFormatted';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const SubContacts = ({ items, linkTo }) => {
  const { t } = useTranslation('messenger', { keyPrefix: 'ChatTitleBarModule' });

  const subContactsLabel = useMemo(() => {
    if (items.length > 0) {
      const [first] = items;
      const name = getFullNameWithTitle(first);
      return t('subContactsLabel', { name, count: items.length });
    }

    return null;
  }, [items, t]);

  if (!items.length) return null;

  if (items.length > 1) {
    return (
      <Menu as="div" className="relative">
        {({ open }) => (
          <>
            <Menu.Button>
              <Badge size={SIZES.SMALL} kind={KINDS.SECONDARY} className="hover:text-blue-500">
                <div className="flex items-center text-sm font-normal space-x-1">
                  <span className="whitespace-nowrap">{subContactsLabel}</span>
                  <ChevronRightIcon className={`w-4 h-4 ${open ? 'rotate-90' : ''}`} />
                </div>
              </Badge>
            </Menu.Button>
            <Menu.Items
              as="ul"
              className="absolute mt-2 origin-top-right divide-y divide-slate-100 rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none z-popup-menu left-1/2 -translate-x-1/2"
            >
              {items.map(subContact => (
                <Menu.Item
                  as="li"
                  key={subContact.id}
                  className="p-3 bg-white hover:bg-slate-50 text-left"
                >
                  <Link
                    to={`/${linkTo}/${subContact.id}${linkTo === 'address-book' ? '/overview' : ''}`}
                  >
                    <UserInfo
                      avatarSize="6"
                      avatarInitials={getInitials(subContact)}
                      title={getFullNameWithTitle(subContact)}
                      subtitle={formatDateOfBirth(subContact.dateOfBirth)}
                    />
                  </Link>
                </Menu.Item>
              ))}
            </Menu.Items>
          </>
        )}
      </Menu>
    );
  }

  return (
    <Badge size={SIZES.SMALL} kind={KINDS.SECONDARY}>
      <Link
        className="hover:text-blue-500 font-normal"
        to={`/${linkTo}/${items[0].id}${linkTo === 'address-book' ? '/overview' : ''}`}
      >
        {subContactsLabel}
      </Link>
    </Badge>
  );
};

SubContacts.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  linkTo: PropTypes.string,
};

SubContacts.defaultProps = {
  items: [],
  linkTo: 'chat',
};

export default SubContacts;
